import { startCase } from 'lodash';
import { csvReportsApi } from '../../../../../services/api';
import { EXPORT_TYPES as EXPORT_TYPE } from '../../../../../services/api/apiConstants';
import { isStaticReport } from '../../../config/isStaticDynamicReport';
import { EXPORT_TYPES } from '../components/GridToolbar/ExportDropdown/constants';
import addLastUpdateTime from '../utils/lastUpdateTimeReportRowTransformer';
import { getDateRangeQueryParam, getReportFiltersParam, getSortingQueryParams } from '../utils/queryFilterUtils';
import { flatDynamicFields } from './dynamicColumnsService';
import { filterReportSortByColumnDefs } from './gridStateService';

const buildFilename = ({
    intl,
    selectedAccountName,
    selectedCampaignName,
    selectedReport,
    startDate,
    endDate,
    buildReportNameForExport,
    reportFilters = [],
    reportConfig,
}) => {
    const selectedCampaignNameText = intl.formatMessage({
        id: selectedCampaignName,
        defaultMessage: selectedCampaignName,
    });
    const selectedFilters = reportFilters
        .map(filter => filter.values.map(item => `-Filter_${filter.type}(${item.label})`))
        .join('');
    const isReportStatic = isStaticReport(selectedReport);
    const reportViewName = isReportStatic
        ? startCase(selectedReport)
        : `${reportConfig?.dynamicReport?.name}(${reportConfig?.dynamicReport?.id})`;

    const selectedReportContext = buildReportNameForExport
        ? buildReportNameForExport(selectedReport, reportConfig)
        : isReportStatic
        ? ` - ${reportViewName} - ${selectedCampaignNameText} - ${startDate.format('MMM-DD-YYYY')} - ${endDate.format(
              'MMM-DD-YYYY'
          )}`
        : ` - ${reportViewName} - ${startDate.format('MMM-DD-YYYY')} - ${endDate.format('MMM-DD-YYYY')}`;
    return `${selectedAccountName}${selectedReportContext}${selectedFilters}.csv`;
};

const transformReportData = ({
    data: { results = [], lastUpdateTime, ...restData },
    lastUpdateTimeField,
    timeZoneName,
}) => {
    const flattenedResults = results.map(({ dynamicFields, ...restRow }) => ({
        ...restRow,
        ...addLastUpdateTime({ rowData: restRow, lastUpdateTimeField, lastUpdateTime, timeZoneName }),
        ...flatDynamicFields(dynamicFields),
    }));

    return { ...restData, results: flattenedResults };
};

const reportsFetcher = (reportUrl, accountId, allQueryParams, lastUpdateTimeField, timeZoneName) => async () => {
    const data = await csvReportsApi(reportUrl, accountId, allQueryParams);
    return transformReportData({ data, lastUpdateTimeField, timeZoneName });
};

export const csvOptionsBuilder = ({
    accountId,
    reportEntityIds,
    reportEntityType,
    dimension,
    selectedReport,
    searchTerm,
    reportSort,
    startDate,
    endDate,
    selectedAccountName,
    selectedCampaignName,
    columnDefs,
    selectedColumns,
    currency,
    intl,
    timeZoneName,
    buildReportNameForExport,
    reportFilters,
    reportQueryParams,
    reportConfig,
    allowedSortingColumnDefs,
    isAsyncExport,
}) => {
    // Build dataProvider func
    const { searchField, sortMap, endpoint, lastUpdateTimeField, postBody } = reportConfig;
    const resolvedReportSort = filterReportSortByColumnDefs(reportSort, columnDefs, allowedSortingColumnDefs);
    const allQueryParams = {
        ...getDateRangeQueryParam(startDate, endDate),
        ...getReportFiltersParam({
            reportEntityIds,
            reportEntityType,
            searchTerm,
            searchField,
            dynamicFilters: reportFilters,
        }),
        ...getSortingQueryParams({ reportSort: resolvedReportSort, sortMap }),
        ...{ [EXPORT_TYPE.EXPORT_TYPE]: isAsyncExport ? EXPORT_TYPES.ASYNC : EXPORT_TYPES.CSV },
        ...reportQueryParams,
        dimension,
        postBody,
    };
    const getAllDataProvider = reportsFetcher(endpoint, accountId, allQueryParams, lastUpdateTimeField, timeZoneName);

    // build filename
    const filename = buildFilename({
        intl,
        selectedAccountName,
        selectedCampaignName,
        selectedReport,
        startDate,
        endDate,
        buildReportNameForExport,
        reportFilters,
        reportConfig,
    });

    // build injectables
    const injectables = { i18n: intl, currency, timeZoneName };

    return {
        columnDefs,
        selectedColumns,
        filename,
        dataProvider: getAllDataProvider,
        injectables,
    };
};
